'use client';

import { useFocusRing } from '@react-aria/focus';

import { FormFieldSlot } from '../../internal/components/form/FormFieldSlot';
import { filterProps } from '../../internal/utils/filterProps';
import { focusRingStyles } from '../../styles/focusRing';
import { type Component, type ComponentProps } from '../../types/component';
import { mergeProps } from '../../utils/mergeProps';
import { tv } from '../../utils/tv';
import { CoreIcon } from '../core-icon/CoreIcon';
import { useTextFieldInternalContext } from './TextFieldContext';

const focusStyles = focusRingStyles();

const textFieldActionClearClasses = {
  root: 'zds-text-field-action-clear',
} as const;

const actionClearStyles = tv({
  slots: {
    root: [
      textFieldActionClearClasses.root,
      'justify-center border-none bg-transparent p-0 font-[inherit]',
    ],
    divider: '',
  },
  variants: {
    hidden: {
      true: { root: 'invisible', divider: 'invisible' },
      false: { root: 'cursor-pointer' },
    },
  },
  defaultVariants: { hidden: false },
});

interface TextFieldActionClearBaseProps {
  /** Accessible label for the clear button (required for i18n). */
  label: string;
  /** Show a divider on the inner side (closest to input). */
  showDivider?: boolean;
}

type TextFieldActionClearProps = ComponentProps<
  TextFieldActionClearBaseProps,
  'button'
>;

type TextFieldActionClearComponent = Component<
  TextFieldActionClearBaseProps,
  'button'
>;

const TextFieldActionClear: TextFieldActionClearComponent = (props) => {
  const [localProps, focusRingRaProps] = filterProps(props, ['useFocusRing']);

  const { label, showDivider, onClick, className, ref, ...restLocalProps } =
    localProps;

  const { inputId, inputRef, value, setValue, isDisabled } =
    useTextFieldInternalContext();
  const { isFocusVisible, focusProps } = useFocusRing(focusRingRaProps);

  const isHidden = value.length === 0 || isDisabled;
  const styles = actionClearStyles({ hidden: isHidden });

  return (
    <FormFieldSlot
      {...mergeProps(restLocalProps, focusProps)}
      component="button"
      ref={ref}
      type="button"
      showDivider={showDivider}
      slotProps={{ divider: { className: styles.divider() } }}
      disabled={isHidden || undefined}
      tabIndex={isHidden ? -1 : 0}
      onClick={(e) => {
        setValue('');
        inputRef.current?.focus();
        onClick?.(e);
      }}
      aria-label={label}
      aria-controls={inputId || undefined}
      aria-hidden={isHidden || undefined}
      data-focus-visible={isFocusVisible || undefined}
      className={focusStyles.root({ className: styles.root({ className }) })}
    >
      <CoreIcon name="closecircleoutlined" size="sm" />
    </FormFieldSlot>
  );
};

TextFieldActionClear.displayName = 'TextField.ActionClear';

export {
  TextFieldActionClear,
  type TextFieldActionClearProps,
  type TextFieldActionClearBaseProps,
  textFieldActionClearClasses,
};
